import { OpeningDetailResponse } from 'api-clients/monolith/models/OpeningDetailResponse';
import { VonqContractPostingRequirements } from 'api-clients/monolith/models/VonqContractPostingRequirements';
import { useMemo } from 'react';

import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';

import { ExpandedView } from './expanded-view';
import { SummaryView } from './summary-view';

import { useWatch } from 'react-hook-form';

interface JobCardProps {
  opening: OpeningDetailResponse;
  postingRequirements: VonqContractPostingRequirements[];
  onRequestRefreshBanner?: () => void;
}

export function JobCard({
  opening,
  postingRequirements,
  onRequestRefreshBanner,
}: JobCardProps) {
  const { getCurrencySymbol } = useCurrencySymbol();
  const { hookForm, selectedOpeningIds } = useJobPostingContext();
  const { watch, setValue } = hookForm;
  const isExpanded = watch(`jobDetails.${opening.external_id}.isExpanded`);

  const channels = useWatch({ control: hookForm.control, name: 'channels' });

  // Filter posting requirements to only contracts that include this opening
  // in their channel's openingBudgets.
  const contractRequirements = useMemo(() => {
    const applicableContractIds = new Set<string>(
      Object.values(channels ?? {})
        .filter(
          ch =>
            ch.vonqDetails?.contractId &&
            opening.external_id in (ch.openingBudgets ?? {}),
        )
        .map(ch => ch.vonqDetails?.contractId as string),
    );
    return postingRequirements.filter(r =>
      applicableContractIds.has(r.contract_id),
    );
  }, [channels, opening.external_id, postingRequirements]);

  const toggleExpanded = () => {
    setValue(`jobDetails.${opening.external_id}.isExpanded`, !isExpanded);

    // Close all other opening cards when one is expanded
    const otherOpeningIds = selectedOpeningIds.filter(
      id => id !== opening.external_id,
    );
    otherOpeningIds.forEach(id => {
      setValue(`jobDetails.${id}.isExpanded`, false);
    });
  };

  const payDisplay = useMemo(() => {
    const currencySymbol = getCurrencySymbol(opening.pay_currency_code);
    const payTypeSuffix = opening.pay_type ? `/${opening.pay_type}` : '';

    if (
      opening.pay_is_range &&
      opening.pay_min != null &&
      opening.pay_max != null
    ) {
      return `${currencySymbol}${opening.pay_min} - ${currencySymbol}${opening.pay_max}${payTypeSuffix}`;
    }

    if (opening.pay != null) {
      return `${currencySymbol}${opening.pay}${payTypeSuffix}`;
    }

    return null;
  }, [getCurrencySymbol, opening]);

  return isExpanded ? (
    <ExpandedView
      opening={opening}
      payDisplay={payDisplay}
      contractRequirements={contractRequirements}
      toggleExpanded={toggleExpanded}
      onRequestRefreshBanner={onRequestRefreshBanner}
    />
  ) : (
    <SummaryView
      opening={opening}
      payDisplay={payDisplay}
      contractRequirements={contractRequirements}
      toggleExpanded={toggleExpanded}
    />
  );
}
